import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';
import { PageLayout } from 'dibs-elements/exports/PageLayout';

import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import { HpRespLocationsModuleItem } from './HpRespLocationsModuleItem';
import { HpRespLocationsModuleItemList } from './HpRespLocationsModuleItemList';
import {
    trackShopByLocationsImpressions,
    trackShopByLocationClick,
} from '../helpers/shopByTracking';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';

import styles from './HpRespLocationsModule.scss';

const COUNTRY_LOCATION_WEIGHT = 200;
const IP_LOCATION_ID = 'ipLocation';

export const handleInitialDisplay = ({ inViewport, locations }) => {
    if (locations && inViewport) {
        locations.map((location, index) => {
            const { cmsId = '', link } = location;
            const isPersonalized = cmsId === IP_LOCATION_ID || '';
            trackShopByLocationsImpressions(index, link, isPersonalized);
        });
    }
};

export const HpRespLocationsModuleComponent = props => {
    const locationsModule = get(props, 'homepage.locationsModule');

    if (!locationsModule) {
        return null;
    }

    const { items, title, subtitle, location } = locationsModule;
    const {
        additionalTopSpacing,
        numOfVisibleLocations,
        showLocationLink,
        pageSetOffset,
        viewer,
    } = props;
    const ipLocationsInfo = get(viewer, 'regionalInfo[0].regionsByZipCode') || [];
    const ipLocationInfo = ipLocationsInfo.find(
        locationInfo =>
            locationInfo.weight === COUNTRY_LOCATION_WEIGHT && locationInfo.locationPageExists
    );
    const ipDisplayName = get(ipLocationInfo, 'displayName') || '';
    const ipUrlLabel = get(ipLocationInfo, 'urlLabel') || '';
    const ipLink = `/locations/${ipUrlLabel}/`;
    let locations = [...items];
    const locationImages = locations.map(({ imageUrl }) => imageUrl);

    if (ipDisplayName && ipUrlLabel && locations && locations.length) {
        locations = locations.filter(loc => loc.link !== ipLink);

        // add ip location to beginning of list, so it appears first in module
        locations.unshift({
            cmsId: IP_LOCATION_ID,
            title: ipDisplayName,
            link: ipLink,
        });
    }

    if (numOfVisibleLocations) {
        locations = locations.slice(0, numOfVisibleLocations);
    }

    return (
        <HpRespModuleContainer>
            <PageLayout setOffset={pageSetOffset} setViewportBackground={'sassyColorWhite'}>
                <HpRespModuleContainer.Header title={title} subtitle={subtitle} />
                <SharedInViewport
                    stopWhenInViewport
                    onInViewportChange={({ inViewport }) =>
                        handleInitialDisplay({ locations: [...locations, location], inViewport })
                    }
                >
                    <div className={`${styles.locations} colXs12`}>
                        {locations.map((item, index) => {
                            return (
                                <HpRespLocationsModuleItem
                                    key={item.cmsId}
                                    item={{ ...item, imageUrl: locationImages[index] }}
                                    showLocation={!!viewer}
                                    showLocationLink={showLocationLink}
                                    onClick={() => trackShopByLocationClick(item, index)}
                                />
                            );
                        })}
                        <HpRespLocationsModuleItemList
                            location={location}
                            ipLink={ipLink}
                            additionalTopSpacing={additionalTopSpacing}
                        />
                    </div>
                </SharedInViewport>
            </PageLayout>
        </HpRespModuleContainer>
    );
};

HpRespLocationsModuleComponent.propTypes = {
    homepage: PropTypes.object,
    viewer: PropTypes.object,
    numOfVisibleLocations: PropTypes.number,
    pageSetOffset: PropTypes.bool,
    showLocationLink: PropTypes.bool,
    additionalTopSpacing: PropTypes.bool,
    truncateSubTitle: PropTypes.bool,
};

export const HpRespLocationsModule = createFragmentContainer(HpRespLocationsModuleComponent, {
    viewer: graphql`
        fragment HpRespLocationsModule_viewer on Viewer {
            regionalInfo(regionTypes: "ITEM_LOCATION") @include(if: $isClient) {
                regionsByZipCode {
                    displayName
                    urlLabel
                    weight
                    locationPageExists
                }
            }
        }
    `,
    homepage: graphql`
        fragment HpRespLocationsModule_homepage on Homepage {
            locationsModule: module(type: "locations") {
                ... on LocationsModule {
                    title
                    subtitle
                    location {
                        title
                        imageUrl
                        items {
                            cmsId
                            title
                            link
                            isLinkable
                        }
                    }
                    items {
                        cmsId
                        title
                        link
                        imageUrl
                    }
                }
            }
        }
    `,
});
